/**
 * Axios 配置
 * 提供简洁的 HTTP 客户端配置
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'
import { Message } from '@arco-design/web-vue'
import { tokenManager } from './token'
import i18n from '@/i18n'

/**
 * 统一响应数据格式（对应后端 Result 类）
 */
export interface ApiResponse<T = any> {
  ok: boolean
  code: string
  message: string
  content: T
  token?: string
}

/**
 * 创建 Axios 实例
 */
function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/gateway',
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // 请求拦截器
  client.interceptors.request.use(
    config => {
      const token = tokenManager.get()
      if (token) {
        config.headers.Authorization = token
      }

      // 根据上下文国际化添加国际化请求头
      config.headers.language = i18n.global.locale.value === 'zh-CN' ? 'zh_CN' : 'en_US'

      console.log(`[TP_API] ${config.method?.toUpperCase()} ${config.url}`)
      return config
    },
    error => {
      console.error('[TP_API_ERROR] 请求错误:', error.message)
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const { data, status } = response

      // 如果是字节流响应（如文件下载、图片等），直接返回
      if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
        return data
      }

      // 如果是文本响应
      if (response.config.responseType === 'text') {
        return data
      }

      // 只处理 HTTP 200
      if (status !== 200) {
        return Promise.reject(new Error(`HTTP ${status}`))
      }

      // HTTP 200 成功
      if (data && typeof data === 'object' && 'ok' in data && 'code' in data && 'message' in data) {
        // 业务成功：ok = true
        if (data.ok === true) {
          // 如果有 token，自动保存
          if (data.token) {
            tokenManager.set(data.token)
            console.log('[TP_API_TOKEN] Token 已更新')
          }

          // 返回 content 字段，如果没有 content 则返回整个响应
          return 'content' in data ? data.content : data
        } else {
          // 业务失败：ok = false
          const errorMessage = data.message || '业务处理失败'
          console.warn(`[TP_API_BUSINESS_ERROR] ${errorMessage}`)

          // 使用 ArcoDesign 的 Message 显示错误提示
          Message.error(errorMessage)

          const error = new Error(errorMessage)
          ;(error as any).code = data.code
          return Promise.reject(error)
        }
      }

      // 非标准格式或其他格式，直接返回数据
      return data
    },
    (error: AxiosError) => {
      const status = error.response?.status
      const message = (error.response?.data as any)?.message || error.message

      // 处理常见 HTTP 错误
      if (status === 401) {
        console.warn('[TP_API_AUTH] 认证失效，请重新登录')
        Message.warning('登录已过期，请重新登录')
        tokenManager.remove()
      } else if (status === 403) {
        console.warn('[TP_API_PERMISSION] 权限不足')
        Message.error('权限不足，无法访问')
      } else if (status === 404) {
        console.warn('[TP_API_NOT_FOUND] 请求的资源不存在')
        Message.error('请求的资源不存在')
      } else if (status && status >= 500) {
        console.error('[TP_API_SERVER] 服务器错误')
        Message.error('服务器错误，请稍后重试')
      } else {
        console.error('[TP_API_ERROR] 请求失败:', message)
        Message.error(message || '请求失败')
      }

      return Promise.reject(error)
    }
  )

  return client
}

// 创建默认实例
export const httpClient = createHttpClient()

// 导出 Token 管理器（从 token.config.ts 重新导出）
export { tokenManager, TokenManager } from './token'

export const request = {
  get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) =>
    httpClient.get<any, T>(url, { params, ...config }),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    httpClient.post<any, T>(url, data, config),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    httpClient.put<any, T>(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    httpClient.patch<any, T>(url, data, config),

  delete: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) =>
    httpClient.delete<any, T>(url, { params, ...config })
}

// 文件上传客户端
export const uploadClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 60000, // 上传超时时间较长
  headers: {
    'Content-Type': 'multipart/form-data'
  }
})

// 上传客户端请求拦截器
uploadClient.interceptors.request.use(config => {
  const token = tokenManager.get()
  if (token) {
    config.headers.Authorization = token
  }
  return config
})
